// Builders for the terminology the code defines: the answer value sets, the
// question and answer concepts with their capture properties, and the links
// from our question codes to LOINC.
import type {
  CodeSystemConcept,
  CodeSystemConceptProperty,
  ConceptMapGroupElement,
  ConceptMapGroupElementTarget,
  ValueSet,
  ValueSetComposeIncludeConcept,
} from '@medplum/fhirtypes'
import { LOINC_CODE_SYSTEM, SEEN_HEALTH_SYSTEM } from '#src/codeSystems.ts'

export const VALUE_SET_BASE = `${SEEN_HEALTH_SYSTEM}/ValueSet`

// Marks a concept as defined here, so the terminology sync owns it.
export const MANAGED_BY_CODE: CodeSystemConceptProperty = {
  code: 'managed-by',
  valueCode: 'code',
}

export type AnswerEntry = [code: string, display: string]

export function answerValueSet(
  name: string,
  title: string,
  system: string,
  concept: ValueSetComposeIncludeConcept[]
): ValueSet {
  return {
    resourceType: 'ValueSet',
    url: `${VALUE_SET_BASE}/${name}`,
    version: '1',
    name: name
      .split('-')
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(''),
    title,
    status: 'active',
    meta: {
      tag: [{ system: `${SEEN_HEALTH_SYSTEM}/managed-by`, code: 'code' }],
    },
    compose: { include: [{ system, concept }] },
  }
}

// Each answer code once, in first-seen order, leaving out codes defined
// elsewhere. `unique` refuses a code given two displays; `first` keeps the
// first, for a set that words a shared code its own way.
export function answerConcepts(
  answers: readonly AnswerEntry[],
  options: { reused?: ReadonlySet<string>; displays: 'unique' | 'first' }
): CodeSystemConcept[] {
  const byCode = new Map<string, string>()
  for (const [code, display] of answers) {
    if (options.reused?.has(code)) {
      continue
    }
    const current = byCode.get(code)
    if (current === undefined) {
      byCode.set(code, display)
    } else if (current !== display && options.displays === 'unique') {
      throw new Error(
        `Answer code ${code} has conflicting displays: ${current} and ${display}`
      )
    }
  }
  return [...byCode].map(([code, display]) => ({
    code,
    display,
    property: [MANAGED_BY_CODE],
  }))
}

export type Capture =
  | { kind: 'choice'; answerSet: string; ordered?: boolean; repeats?: boolean }
  | { kind: 'text' }
  | { kind: 'date' }
  | { kind: 'quantity'; unit: string }

export function captureProperties(
  capture: Capture
): CodeSystemConceptProperty[] {
  switch (capture.kind) {
    case 'choice':
      return [
        { code: 'SCALE_TYP', valueString: capture.ordered ? 'Ord' : 'Nom' },
        { code: 'answer-list', valueString: capture.answerSet },
        ...(capture.repeats
          ? [{ code: 'repeats', valueBoolean: true as const }]
          : []),
      ]
    case 'text':
      return [{ code: 'SCALE_TYP', valueString: 'Nar' }]
    case 'date':
      return [
        { code: 'SCALE_TYP', valueString: 'Qn' },
        { code: 'value-kind', valueCode: 'date' },
      ]
    case 'quantity':
      return [
        { code: 'SCALE_TYP', valueString: 'Qn' },
        { code: 'unit', valueString: capture.unit },
      ]
  }
}

export function loincRelatedProperties(
  code: string | undefined
): CodeSystemConceptProperty[] {
  return code
    ? [
        {
          code: 'loinc-related',
          valueCoding: { system: LOINC_CODE_SYSTEM, code },
        },
      ]
    : []
}

export interface LoincLink {
  code: string
  equivalence: 'equivalent' | 'wider' | 'relatedto'
  comment: string
}

// A question a clinical template asks, with how it is captured and how it
// relates to LOINC.
export interface TemplateQuestion {
  code: string
  display: string
  definition: string
  domain: string
  capture: Capture
  // Absent when LOINC was searched and has nothing that fits.
  loinc?: LoincLink
  unmatched?: string
}

export function templateQuestionConcept(
  question: TemplateQuestion
): CodeSystemConcept {
  return {
    code: question.code,
    display: question.display,
    definition: question.definition,
    property: [
      ...loincRelatedProperties(question.loinc?.code),
      { code: 'domain', valueCode: question.domain },
      ...captureProperties(question.capture),
      MANAGED_BY_CODE,
    ],
  }
}

export function templateQuestionLoincMap(
  question: TemplateQuestion
): ConceptMapGroupElement {
  return {
    code: question.code,
    target: [
      question.loinc
        ? {
            code: question.loinc.code,
            equivalence: question.loinc.equivalence,
            comment: question.loinc.comment,
          }
        : ({
            equivalence: 'unmatched',
            comment: question.unmatched,
          } satisfies ConceptMapGroupElementTarget),
    ],
  }
}
